/* eslint-disable default-case */
import React, { useLayoutEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import config from '../app/config';
import { checkObsStatus, mapObservationFromAPI } from '../pages/ward/forms/observation/obsUtils';
import { UtilsMomentJS } from '../store/utilsMoment';
import ReactDOMServer from 'react-dom/server';

export function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([document.documentElement.clientWidth, document.documentElement.clientHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}

class Formatter {
    static formatUserFullName(userObject) {
        return `${userObject.familyName}, ${userObject.firstName}`;
    }

    static formatStringDate(t, date) {
        if (!date) {
            return t('Date unspecified');
        }
        const mmt = moment(date);
        return (
            <span>
                <strong>{mmt.format(config.secondaryDateFormat)}</strong>
                {mmt.format(', ' + config.secondaryDateFormatSupliment)}
            </span>
        );
    }

    static formatBirthDate(dateValue) {
        if (!moment.isMoment(dateValue)) dateValue = moment(dateValue);
        return dateValue.format(config.defaultDateFormat);
    }

    static formatPatientAge(dob) {
        const age = moment().diff(moment(dob), 'years');
        const months = moment().diff(moment(dob), 'months');
        return age >= 1 ? age : `${months}M`;
    }

    static formatPersonName(t, personName) {
        let person = personName;

        if (!personName) {
            person = t('Unknown');
        }

        const names = [];
        const parts = person.split('^');
        _.forEach(parts, (part) => {
            if (part && part.length > 0) {
                names.push(part);
            }
        });

        // person = person.replaceAll("^", ", ");
        // return person;
        return this.formatSetAsString(Array.from(names), true);
    }

    static getStudyLocation(t, study) {
        let locations = new Set();
        _.forEach(study.documents, (document) => {
            locations.add(document.metadata && document.metadata.location ? document.metadata.location : t('Location not set'));
        });

        return this.formatSetAsString(Array.from(locations));
    }

    static getStudyOperator(t, study) {
        let operators = new Set();
        _.forEach(study.documents, (document) => {
            // operators.add(document.metadata.patientName ? Formatter.formatPersonName(t, document.metadata.patientName) : t("Operator not set"));
            operators.add(
                document.metadata && document.metadata.operatorName
                    ? Formatter.formatPersonName(t, document.metadata.operatorName)
                    : t('Operator not set'),
            );
        });

        return this.formatSetAsString(Array.from(operators));
    }

    static getStudyType(t, study) {
        let types = new Set();
        _.forEach(study.documents, (document) => {
            types.add(document.metadata && document.metadata.procedureName ? document.metadata.procedureName : t('NO TYPE'));
        });

        return this.formatSetAsString(Array.from(types));
    }

    static getColorByType(t, study) {
        let types = new Set();
        _.forEach(study.documents, (document) => {
            types.add(document.metadata && document.metadata.color ? document.metadata.color : config.defaultColor);
        });

        return Array.from(types);
    }

    static getStudyData(t, study) {
        let patientIDs = new Set();
        let studyNames = new Set();
        _.forEach(study.documents, (document) => {
            patientIDs.add(document?.metadata?.patientId ? document.metadata.patientId.toUpperCase() : t('Unknown'));
            studyNames.add(document?.metadata?.research?.studyName ? document.metadata.research.studyName : t('Unknown'));
        });

        return {
            __patientId: this.formatSetAsString(Array.from(patientIDs)),
            __researchStudyName: this.formatSetAsString(Array.from(studyNames)),
        };
    }

    static formatSetAsString(toBeExtracted, skipSorting) {
        let setToExtract = toBeExtracted;
        if (!skipSorting) {
            setToExtract = _.sortBy(setToExtract);
        }

        let setAsString = '';
        for (let operator of setToExtract) {
            if (setAsString.length > 0) {
                setAsString += ', ';
            }
            setAsString += operator;
        }
        return setAsString;
    }

    static formatLaterality(laterality) {
        let lateralityTranslated = laterality;
        switch (laterality) {
            case 'R':
                lateralityTranslated = 'OD';
                break;
            case 'L':
                lateralityTranslated = 'OS';
                break;
            case 'B':
                lateralityTranslated = 'OU';
                break;
        }

        return lateralityTranslated;
    }

    static formatLateralityEx(laterality) {
        let lateralityTranslated;
        switch (laterality) {
            case 'R':
                lateralityTranslated = 'OD';
                break;
            case 'L':
                lateralityTranslated = 'OS';
                break;
            case 'B':
                lateralityTranslated = 'OU';
                break;
            default:
                lateralityTranslated = null;
                break;
        }

        return lateralityTranslated;
    }

    static formatProcedureNameSimple(t, procedureName) {
        return procedureName ? procedureName : t('UNK');
    }

    static formatProcedureName(t, procedureName) {
        let text = t('Unknown procedure');

        switch (procedureName) {
            case 'AR':
                text = t('Autorefraction');
                break;
            case 'ASMT':
                text = t('Content Assessment Results');
                break;
            case 'AU':
                text = t('Audio');
                break;
            case 'BDUS':
                text = t('Bone Densitometry (ultrasound)');
                break;
            case 'BI':
                text = t('Biomagnetic imaging');
                break;
            case 'BMD':
                text = t('Bone Densitometry (X-Ray)');
                break;
            case 'CR':
                text = t('Computed Radiography');
                break;
            case 'CT':
                text = t('Computed Tomography');
                break;
            case 'CTPROTOCOL':
                text = t('CT Protocol (Performed)');
                break;
            case 'DG':
                text = t('Diaphanography');
                break;
            case 'DOC':
                text = t('Document');
                break;
            case 'DX':
                text = t('Digital Radiography');
                break;
            case 'ECG':
                text = t('Electrocardiography');
                break;
            case 'EPS':
                text = t('Cardiac Electrophysiology');
                break;
            case 'ES':
                text = t('Endoscopy');
                break;
            case 'FID':
                text = t('Fiducials');
                break;
            case 'GM':
                text = t('General Microscopy');
                break;
            case 'HC':
                text = t('Hard Copy');
                break;
            case 'HD':
                text = t('Hemodynamic Waveform');
                break;
            case 'IO':
                text = t('Intra-Oral Radiography');
                break;
            case 'IOL':
                text = t('Intraocular Lens Data');
                break;
            case 'IVOCT':
                text = t('Intravascular Optical Coherence Tomography');
                break;
            case 'IVUS':
                text = t('Intravascular Ultrasound');
                break;
            case 'KER':
                text = t('Keratometry');
                break;
            case 'KO':
                text = t('Key Object Selection');
                break;
            case 'LEN':
                text = t('Lensometry');
                break;
            case 'LS':
                text = t('Laser surface scan');
                break;
            case 'MG':
                text = t('Mammography');
                break;
            case 'MR':
                text = t('Magnetic Resonance');
                break;
            case 'M3D':
                text = t('Model for 3D Manufacturing');
                break;
            case 'NM':
                text = t('Nuclear Medicine');
                break;
            case 'OAM':
                text = t('Ophthalmic Axial Measurements');
                break;
            case 'OCT':
                text = t('Optical Coherence Tomography (non-Ophthalmic)');
                break;
            case 'OP':
                text = t('Ophthalmic Photography');
                break;
            case 'OPM':
                text = t('Ophthalmic Mapping');
                break;
            case 'OPT':
                text = t('Ophthalmic Tomography');
                break;
            case 'OPTBSV':
                text = t('Ophthalmic Tomography B-scan Volume Analysis');
                break;
            case 'OPTENF':
                text = t('Ophthalmic Tomography En Face');
                break;
            case 'OPV':
                text = t('Ophthalmic Visual Field');
                break;
            case 'OSS':
                text = t('Optical Surface Scan');
                break;
            case 'OT':
                text = t('Other');
                break;
            case 'PLAN':
                text = t('Plan');
                break;
            case 'PR':
                text = t('Presentation State');
                break;
            case 'PT':
                text = t('Positron emission tomography (PET)');
                break;
            case 'PX':
                text = t('Panoramic X-Ray');
                break;
            case 'REG':
                text = t('Registration');
                break;
            case 'RESP':
                text = t('Respiratory Waveform');
                break;
            case 'RF':
                text = t('Radio Fluoroscopy');
                break;
            case 'RG':
                text = t('Radiographic imaging (conventional film/screen)');
                break;
            case 'RTDOSE':
                text = t('Radiotherapy Dose');
                break;
            case 'RTIMAGE':
                text = t('Radiotherapy Image');
                break;
            case 'RTINTENT':
                text = t('Radiotherapy Intent');
                break;
            case 'RTPLAN':
                text = t('Radiotherapy Plan');
                break;
            case 'RTRAD':
                text = t('RT Radiation');
                break;
            case 'RTRECORD':
                text = t('RT Treatment Record');
                break;
            case 'RTSEGANN':
                text = t('Radiotherapy Segment Annotation');
                break;
            case 'RTSTRUCT':
                text = t('Radiotherapy Structure Set');
                break;
            case 'RWV':
                text = t('Real World Value Map');
                break;
            case 'SEG':
                text = t('Segmentation');
                break;
            case 'SM':
                text = t('Slide Microscopy');
                break;
            case 'SMR':
                text = t('Stereometric Relationship');
                break;
            case 'SR':
                text = t('SR Document');
                break;
            case 'SRF':
                text = t('Subjective Refraction');
                break;
            case 'STAIN':
                text = t('Automated Slide Stainer');
                break;
            case 'TEXTUREMAP':
                text = t('Texture Map');
                break;
            case 'TG':
                text = t('Thermography');
                break;
            case 'US':
                text = t('Ultrasound');
                break;
            case 'VA':
                text = t('Visual Acuity');
                break;
            case 'XA':
                text = t('X-Ray Angiography');
                break;
            case 'XC':
                text = t('External-camera Photography');
                break;
            case 'AS':
                text = t('Angioscopy');
                break;
            case 'CD':
                text = t('Color flow Doppler');
                break;
            case 'CF':
                text = t('Cinefluorography');
                break;
            case 'CP':
                text = t('Culposcopy');
                break;
            case 'CS':
                text = t('Cystoscopy');
                break;
            case 'DD':
                text = t('Duplex Doppler');
                break;
            case 'DF':
                text = t('Digital fluoroscopy');
                break;
            case 'DM':
                text = t('Digital microscopy');
                break;
            case 'DS':
                text = t('Digital Subtraction Angiography');
                break;
            case 'EC':
                text = t('Echocardiography');
                break;
            case 'FA':
                text = t('Fluorescein angiography');
                break;
            case 'FS':
                text = t('Fundoscopy');
                break;
            case 'LP':
                text = t('Laparoscopy');
                break;
            case 'MA':
                text = t('Magnetic resonance angiography');
                break;
            case 'MS':
                text = t('Magnetic resonance spectroscopy');
                break;
            case 'OPR':
                text = t('Ophthalmic Refraction');
                break;
            case 'ST':
                text = t('Single-photon emission computed tomography (SPECT)');
                break;
            case 'VF':
                text = t('Videofluorography');
                break;
        }

        return text;
    }

    static dateFormatted(valueDate, format) {
        let mStr = '';
        try {
            if (valueDate) {
                mStr = moment(valueDate).format(format);
            }
        } catch (e) {
            console.error(e);
        }
        return mStr;
    }

    static formatBooleanAsString(value) {
        return value ? 'Yes' : 'No';
    }

    static formatObservationToText(obs) {
        const getVal = (object, field, isBoolean) => {
            if (!object || !field) return '-';

            const checkStatus = checkObsStatus(object);
            if (checkStatus === 'completed') {
                const val = _.get(object, field);

                if (!_.isNull(val)) {
                    if (isBoolean) {
                        return val ? 'Yes' : 'No';
                    }
                    return val;
                }

                return '-';
            } else {
                return checkStatus;
            }
        };

        if (obs) {
            let mappedObs = mapObservationFromAPI(obs);
            let text = `Observation started on ${mappedObs?.startedDate.format(config.defaultDateTimeFormat)} by ${mappedObs?.completedByFullName}\n`;
            text += `${_.toUpper(mappedObs?.investigation)} Score ${mappedObs?.totalScore}\n\n`;
            text += `Respiratory Rate: ${getVal(mappedObs?.respiration, 'breaths')}\n`;
            text += `Inspired O₂%: ${_.capitalize(getVal(mappedObs?.airOxygen, 'airType'))}\n`;
            if (mappedObs?.investigation !== 'news') {
                text += `Respiratory Distresses: ${getVal(mappedObs?.distress, 'distress.description')}\n`;
                text += `Respiratory Distress Wheezing: ${getVal(mappedObs?.wheezing, 'wheezing.description')}\n`;
                text += `Stridor: ${getVal(mappedObs?.stridor, 'stridor', true)}\n`;
                text += `Apnoea: ${getVal(mappedObs?.apnoea, 'apnoea', true)}\n`;
            }
            text += `SpO₂: ${getVal(mappedObs?.spo2scale, 'spo2scale')}\n`;
            text += `Hypercapnic: ${getVal(mappedObs?.spo2scale, 'hypercapnic', true)}\n`;
            text += `Blood Pressure Systolic: ${getVal(mappedObs?.bloodPressure, 'systolicPressure')}\n`;
            text += `Blood Pressure Diastolic: ${getVal(mappedObs?.bloodPressure, 'diastolicPressure')}\n`;
            text += `Heart Rate: ${getVal(mappedObs?.pulseRate, 'pulseRate')} | Type: ${_.capitalize(getVal(mappedObs?.pulseRate, 'pulseType'))}\n`;
            text += `Consciousness Level: ${getVal(mappedObs?.consciousness, 'consciousnessLevel.description')}\n`;
            if (mappedObs?.investigation !== 'news') {
                text += `CRT ${getVal(mappedObs?.crt, 'crt')}\n`;
            }
            text += `Temperature: ${getVal(mappedObs?.temperature, 'temperature')}\n`;
            text += `Pain Score: ${getVal(mappedObs?.painScore, 'painScore')}\n`;
            text += `Glucose: ${getVal(mappedObs?.glucose, 'glucose')}\n`;
            text += `Ketones: ${getVal(mappedObs?.glucose, 'ketones')}\n`;
            text += `Sepsis Suspected: ${mappedObs?.extraOption3 ? 'Yes' : 'No'}\n`;
            text += `Status: ${_.startCase(mappedObs?.status)}\n`;
            if (mappedObs?.status === 'completed') {
                text += `Completed by ${mappedObs?.completedByFullName}`;
            }

            return text;
        }
        return '';
    }

    static formatObservationsMapToText(obsMap, type) {
        const onlyPews = ['Respiratory Distresses', 'Respiratory Distress Wheezing', 'Stridor', 'Apnoea', 'CRT'];

        const getVal = (arr) => {
            let text = '';
            _.forEach(arr, (obj) => (text += `${obj} | `));

            return text;
        };

        if (!_.isEmpty(obsMap.get('Started'))) {
            let note = '\nObservations Comparison \n';

            obsMap.forEach((val, key) => {
                if (type === 'pews' ? true : !onlyPews.includes(key)) {
                    note += `${key}: ${getVal(val)}\n`;
                }
            });
            return note;
        }
    }

    static formatDiagnosisTreatmentInvestigationToNote(diagnosis, treatments, investigations, prefix = '') {
        const getLocalDateText = (obj, type) => {
            const local = UtilsMomentJS.getLocalDateFromUTCECDSFormat(obj.procedureDate, obj.procedureTime);

            if (local) {
                return type === 'date' ? local.format(config.defaultDateFormat) : local.format(config.defaultTimeFormat);
            }
            return '-';
        };
        const textPrefix = prefix && prefix !== '' ? `${prefix} ` : '';
        let text = '';

        if (!_.isEmpty(diagnosis)) {
            text += `|${textPrefix}Diagnosis|Procedure Date and Time|Diagnosis Qualifier|Sequence Number|\n|:---|:---:|:---:|:---:|\n`;
            _.forEach(diagnosis, (element) => {
                if (element.value) {
                    text += `|${element.value[element?.value?.diagnosisMax ? 'snomedFullySpecifiedName' : 'snomedDescription']}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|`;
                    text += `${element.qualifier !== null ? element.qualifier.snomedDescription : '----'}|${element.sequenceNumber === '' ? '----' : element.sequenceNumber}|\n`;
                }
            });
            text += `\n`;
        }

        if (!_.isEmpty(treatments)) {
            text += `|${textPrefix}Treatment|Procedure Date and Time|\n|:---|:---:|\n`;
            _.forEach(treatments, (element) => {
                if (element.value) {
                    text += `|${element.value.__description}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|\n`;
                }
            });
            text += `\n`;
        }

        if (!_.isEmpty(investigations)) {
            text += `|${textPrefix}Investigation|Procedure Date and Time|\n|:---|:---:|\n`;
            _.forEach(investigations, (element) => {
                if (element.value) {
                    text += `|${element.value.__description}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|\n`;
                }
            });
            text += `\n`;
        }
        return text;
    }

    static formatSafeguardingToNote(safeguarding) {
        let text = '';

        if (!_.isEmpty(safeguarding)) {
            text += `|Name|Code|\n|:---|:---|\n`;
            _.forEach(safeguarding, (element) => {
                text += `|${element.__description}|${element.__code}|\n`;
            });
            text += `\n\n`;
        }
        return text;
    }

    static getSvgPath(icon) {
        let iconPath = '';

        const parser = new DOMParser();
        const iconString = ReactDOMServer.renderToString(icon);
        const svgDoc = parser.parseFromString(iconString, 'image/svg+xml');

        _.forEach(svgDoc.querySelectorAll('path'), (path) => {
            const att = path?.getAttribute('d');
            if (att) iconPath += att + ' ';
        });

        _.forEach(svgDoc.querySelectorAll('circle'), (path) => {
            const cx = path?.getAttribute('cx');
            const cy = path?.getAttribute('cy');
            const r = path?.getAttribute('r');
            if (cx && cy && r) {
                const att = `M ${cx}, ${cy} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
                iconPath += att + ' ';
            }
        });

        return iconPath;
    }

    static fragmentCanvasText(ctx, text, maxWidth) {
        let words = text.split(' '),
            lines = [],
            line = '';
        if (ctx.measureText(text).width < maxWidth) {
            return [text];
        }
        while (words.length > 0) {
            let split = false;
            while (ctx.measureText(words[0]).width >= maxWidth) {
                let tmp = words[0];
                words[0] = tmp.slice(0, -1);
                if (!split) {
                    split = true;
                    words.splice(1, 0, tmp.slice(-1));
                } else {
                    words[1] = tmp.slice(-1) + words[1];
                }
            }
            if (ctx.measureText(line + words[0]).width < maxWidth) {
                line += words.shift() + ' ';
            } else {
                lines.push(line);
                line = '';
            }
            if (words.length === 0) {
                lines.push(line);
            }
        }
        return lines;
    }

    static UtcFormatData(d, format = 'YYYY-MM-DD HH:mm', postfix = ' (UTC)') {
        if (d) {
            return moment.utc(d).format(format) + postfix;
        }
        return '-';
    }

    static LocalFormatData(d, format = 'YYYY-MM-DD HH:mm', postfix = ' (L)') {
        if (d) {
            return moment(d).format(format) + postfix;
        }
        return '-';
    }

    static utcAndLocalFormatData(d, format = 'YYYY-MM-DD HH:mm', postfix = ' (UTC)', isMilisecondTime = false) {
        if (d) {
            let tmpD = d;
            if (isMilisecondTime) {
                tmpD = moment(d, 'x');
            }
            const local = Formatter.LocalFormatData(tmpD, format, postfix);
            const utc = Formatter.UtcFormatData(tmpD, format, postfix);
            if (local === '-' && utc === '-') return '-';
            if (local !== utc) return local + '(L) | ' + utc + '(UTC)';
            else {
                return utc + ' (L)(UTC)';
            }
        }
        return '-';
    }

    static setToString(set, delim) {
        let str = '';
        set.forEach(function (elem) {
            str += elem + delim;
        });
        return str.slice(0, -1);
    }

    static getClinicalConclusionStatusText = (status) => {
        switch (status) {
            case 'Discharge':
                return 'Ready to Discharge';
            case 'Deceased':
                return 'Deceased';
            case 'Transfer':
                return 'Ready to Transfer';
            case 'Admitted':
                return 'Ready to Admit';
            default:
                return status;
        }
    };

    static getClinicalConclusionShortStatusText = (status) => {
        switch (status) {
            case 'Discharge':
                return 'D';
            case 'Transfer':
                return 'T';
            case 'Admitted':
                return 'A';
            default:
                return null;
        }
    };

    static generateRandomString = (length) => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let result = '';
        const charactersLength = characters.length;

        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }

        return result;
    };

    static getGPFullName = (gp) => {
        if (!gp) return null;
        const { title = '', firstName = '', middleNames = '', familyName = '' } = gp;
        return `${title ?? ''} ${firstName} ${middleNames ?? ''} ${familyName}`.trim();
    };
}

export default Formatter;
